// data-fit LLC · DecisionFlow · ADMIN Stage
// PlanBuilder.jsx · DDL: R20 · API: v1.27 · S247 Phase 10
// Auth: useAuth() direct — ARCH-2 compliant
// Patched: Session 91 — PATCH-03 (window.prompt replaced with inline rejection reason state)
// Patched: S205 — CSS fix
// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
// S247 Phase 10:
//   - OI-AMENDMENT-UI-BEGIN-MODAL: AmendmentBeginModal fires before createPlanComponent + approvePlanComponent
//   - OI-AMENDMENT-UI-COMMIT-ON-SAVE: amendment committed after successful component save/approve
//   - governed_entity_type = "plan_component"
//   - Plan header create (POST /plans) is NOT a governed entity — no amendment required
//   - Props added: fiscalPeriodId (passed through), wizardSessionId
// S496 fix — OI-PLAN-COMPONENT-APPROVE-AMENDMENT-GATE-BUG:
//   - handleApproveComponentClick: amendment gate now conditional on approved_by IS NOT NULL
//   - First-time approval (draft component, approved_by=NULL) bypasses amendment — direct approve
//   - Re-approval of governed component (approved_by set) still requires amendment as before

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { API_BASE } from './DataSetupFlowShell';
import AmendmentBeginModal from '../admin/AmendmentBeginModal.jsx';
import { Lock, ChevronDown, Upload } from 'lucide-react';

const GOVERNED_ENTITY_TYPE = 'plan_component';
const FLOAT_TOLERANCE = 1e-4;

const EMPTY_HEADER_FORM = { role_id: '', segment_id: '', fiscal_period_id: '', plan_code: '', plan_name: '', business_unit_id: '', rollup_mode: '' };
const EMPTY_COMPONENT_FORM = { ote_min: '', ote_max: '', ote_midpoint: '', ote_currency_code: '', base_pct: '', variable_pct: '', arr_weight: '', mbo_weight: '', nps_weight: '', adoption_weight: '', effective_date: '' };

function toIntegerOrNull(v) { if (v === '' || v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function toNumberOrNull(v)  { if (v === '' || v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function nearlyEqual(a, b, tol = FLOAT_TOLERANCE) { return Math.abs(Number(a) - Number(b)) <= tol; }
function getErrorMessage(error, fallback) { return error instanceof Error && error.message ? error.message : fallback; }

async function commitAmendment(amendmentId) {
  if (!amendmentId) return;
  try {
    const res = await fetch(`${API_BASE}/amendments/${amendmentId}/commit`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) { const b = await res.json().catch(() => ({})); console.warn('[Amendment] Commit failed:', b?.error?.message ?? res.status); }
  } catch (e) { console.warn('[Amendment] Commit network error:', e.message); }
}

function PlanBuilder({ tier = 'essentials', onBack, onNext, fiscalPeriodId: propFiscalPeriodId, wizardSessionId, isLocked, initialPlanId, initialComponentDefaults, onConsumed, embedded = false, initialFocusField = null }) {
  const { user } = useAuth();
  const hasBusinessUnitScope = tier === 'standard' || tier === 'enterprise';
  const isEnterprise = tier === 'enterprise';

  const [plans, setPlans]                       = useState([]);
  const [planComponents, setPlanComponents]     = useState([]);
  const [roles, setRoles]                       = useState([]);
  const [segments, setSegments]                 = useState([]);
  const [fiscalPeriods, setFiscalPeriods]       = useState([]);
  const [businessUnits, setBusinessUnits]       = useState([]);
  const [selectedPlanId, setSelectedPlanId]     = useState(null);
  const [step, setStep]                         = useState(embedded ? 2 : null); // null = no form shown; 1 = new plan header; 2 = component form
  const [csvImportOpen, setCsvImportOpen]       = useState(false); // bulk import collapsed by default
  const [headerForm, setHeaderForm]             = useState(EMPTY_HEADER_FORM);
  const [componentForm, setComponentForm]       = useState(EMPTY_COMPONENT_FORM);
  const [loading, setLoading]                   = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [inlineError, setInlineError]           = useState('');
  const [blockingError, setBlockingError]       = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [toast, setToast]                       = useState(null);
  const [rejectingPlanId, setRejectingPlanId]   = useState(null);
  const [rejectionReason, setRejectionReason]   = useState('');
  const [editingComponentId, setEditingComponentId] = useState(null); // plan_component_id when in edit mode, null when creating

  // S471 OI-PLANS-WEBAPP-IMPORT — CSV bulk import state
  // POST /api/v1/import/upload, file_type=plan
  // Two-step: Validate → Confirm and load
  // Plans created as draft, source_type='webapp'. Two-actor approval required after import.
  const [csvPlanFile, setCsvPlanFile]           = useState(null);
  const [csvPlanDryResult, setCsvPlanDryResult] = useState(null);
  const [csvPlanCommitted, setCsvPlanCommitted] = useState(false);
  const [csvPlanImporting, setCsvPlanImporting] = useState(false);
  const [csvPlanError, setCsvPlanError]         = useState('');

  // Amendment state — governs plan_component create + approve
  const [amendModalOpen, setAmendModalOpen] = useState(false);
  const [amendmentId, setAmendmentId]       = useState(null);
  const [pendingAction, setPendingAction]   = useState(null); // 'create_component' | { type: 'approve_component', id }

  const showToast = useCallback((msg) => { setToast(msg); window.setTimeout(() => setToast(null), 3000); }, []);

  const fetchJson = useCallback(async (url, options = {}) => {
    const res = await fetch(url, { ...options, credentials: 'include', headers: { ...(options.headers || {}) } });
    if (!res.ok) { const msg = await res.text(); throw new Error(msg || `Request failed with status ${res.status}`); }
    if (res.status === 204) return null;
    return res.json();
  }, []);

  const loadPlans = useCallback(async () => {
    if (!user) return;
    setLoading(true); setInlineError('');
    try { const d = await fetchJson(`${API_BASE}/plans`); setPlans(Array.isArray(d) ? d : d?.plans || d?.data || []); }
    catch (e) { setInlineError(getErrorMessage(e, 'Unable to fetch plans.')); }
    finally { setLoading(false); }
  }, [fetchJson, user]);

  const loadPlanComponents = useCallback(async () => {
    if (!user) return;
    try { const d = await fetchJson(`${API_BASE}/plan-components`); setPlanComponents(Array.isArray(d) ? d : d?.plan_components || d?.data || []); }
    catch (e) { setInlineError(getErrorMessage(e, 'Plan component endpoint unavailable.')); }
  }, [fetchJson, user]);

  const loadReferenceData = useCallback(async () => {
    if (!user) return;
    setReferenceLoading(true); setBlockingError('');
    try {
      const [rolesData, segsData, fpData, buData] = await Promise.all([
        fetchJson(`${API_BASE}/roles`),
        fetchJson(`${API_BASE}/segments`),
        fetchJson(`${API_BASE}/fiscal-periods`),
        hasBusinessUnitScope ? fetchJson(`${API_BASE}/business-units`) : Promise.resolve([]),
      ]);
      setRoles(Array.isArray(rolesData) ? rolesData : rolesData?.roles || rolesData?.data || []);
      setSegments(Array.isArray(segsData) ? segsData : segsData?.segments || segsData?.data || []);
      setFiscalPeriods(Array.isArray(fpData) ? fpData : fpData?.fiscal_periods || fpData?.data || []);
      setBusinessUnits(Array.isArray(buData) ? buData : buData?.business_units || buData?.data || []);
    } catch (e) { setBlockingError(getErrorMessage(e, 'Unable to fetch required plan reference data.')); }
    finally { setReferenceLoading(false); }
  }, [fetchJson, hasBusinessUnitScope, user]);

  useEffect(() => { loadReferenceData(); }, [loadReferenceData]);
  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => { loadPlanComponents(); }, [loadPlanComponents]);

  // S680: pre-select plan and seed component form when arriving from Plan Library deploy.
  // Fires once after plans load — initialPlanId identifies the newly-deployed draft plan.
  // initialComponentDefaults pre-populates base_pct, variable_pct, weights from certified
  // package snapshot. OTE fields left blank — always a manual CA decision (§17).
  // onConsumed clears pendingComponentConfig in CompensationPoliciesWorkspace so this
  // effect does not re-fire when PlanBuilder re-renders.
  useEffect(() => {
    if (!initialPlanId || plans.length === 0) return;
    const match = plans.find((p) => String(p.plan_id) === String(initialPlanId));
    if (!match) return;
    setSelectedPlanId(match.plan_id);
    setStep(2);
    if (initialComponentDefaults && Object.keys(initialComponentDefaults).length > 0) {
      const d = initialComponentDefaults;
      setComponentForm({
        ote_min:           '',
        ote_max:           '',
        ote_midpoint:      '',
        ote_currency_code: '',
        base_pct:          d.base_pct        != null ? String(d.base_pct)        : '',
        variable_pct:      d.variable_pct    != null ? String(d.variable_pct)    : '',
        arr_weight:        d.arr_weight      != null ? String(d.arr_weight)      : '',
        mbo_weight:        d.mbo_weight      != null ? String(d.mbo_weight)      : '',
        nps_weight:        d.nps_weight      != null ? String(d.nps_weight)      : '',
        adoption_weight:   d.adoption_weight != null ? String(d.adoption_weight) : '',
        effective_date:    '',
      });
    }
    if (onConsumed) onConsumed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPlanId, plans]);

  const updateHeaderForm = useCallback((f, v) => setHeaderForm((p) => ({ ...p, [f]: v })), []);
  const updateComponentForm = useCallback((f, v) => setComponentForm((p) => ({ ...p, [f]: v })), []);

  const selectedPlan = useMemo(() => plans.find((p) => String(p.plan_id) === String(selectedPlanId)) || null, [plans, selectedPlanId]);

  const roleNameById           = useMemo(() => roles.reduce((a, r) => { a[String(r.role_id)] = r.role_name || r.name; return a; }, {}), [roles]);
  const segmentNameById        = useMemo(() => segments.reduce((a, s) => { a[String(s.segment_id)] = s.segment_name || s.name; return a; }, {}), [segments]);
  const fiscalPeriodNameById   = useMemo(() => fiscalPeriods.reduce((a, p) => { a[String(p.fiscal_period_id)] = p.period_label || `Period ${p.fiscal_period_id}`; return a; }, {}), [fiscalPeriods]);
  const businessUnitNameById   = useMemo(() => businessUnits.reduce((a, b) => { a[String(b.business_unit_id)] = b.business_unit_name || b.name; return a; }, {}), [businessUnits]);

  const validateHeader = useCallback(() => {
    const e = {};
    if (!headerForm.role_id)             e.role_id = 'Role is required.';
    if (!headerForm.segment_id)          e.segment_id = 'Segment is required.';
    if (!headerForm.fiscal_period_id)    e.fiscal_period_id = 'Fiscal period is required.';
    if (!headerForm.plan_code.trim())    e.plan_code = 'Plan code is required.';
    if (!headerForm.plan_name.trim())    e.plan_name = 'Plan name is required.';
    return e;
  }, [headerForm]);

  const validateComponent = useCallback(() => {
    const e = {};
    const oteMin      = toNumberOrNull(componentForm.ote_min);
    const oteMax      = toNumberOrNull(componentForm.ote_max);
    const oteCurrency = componentForm.ote_currency_code?.trim() || null;
    const base = toNumberOrNull(componentForm.base_pct);
    const variable = toNumberOrNull(componentForm.variable_pct);
    const arr = toNumberOrNull(componentForm.arr_weight);
    const mbo = toNumberOrNull(componentForm.mbo_weight);
    const nps = toNumberOrNull(componentForm.nps_weight);
    const adop = toNumberOrNull(componentForm.adoption_weight);
    // §17 DR-027: OTE is optional — all four fields NULL is valid (no annotation).
    // Partial band (some but not all fields provided) = PARTIAL_OTE_BAND error.
    const oteFieldsProvided = [oteMin, oteMax, oteCurrency].filter((v) => v !== null).length;
    if (oteFieldsProvided > 0 && oteFieldsProvided < 3) {
      e.ote_band = 'OTE band is partial. Provide OTE Min, OTE Max, and Currency together — or leave all blank.';
    }
    if (oteMin !== null && oteMax !== null && oteMin > oteMax) e.ote_max = 'OTE maximum must be >= OTE minimum.';
    if (base === null) e.base_pct = 'Base percent is required.';
    if (variable === null) e.variable_pct = 'Variable percent is required.';
    if (base !== null && variable !== null && !nearlyEqual(base + variable, 1.0)) e.base_variable_sum = 'Base + variable must equal 1.0.';
    if ([arr, mbo, nps, adop].some((v) => v === null)) e.weight_sum = 'ARR, MBO, NPS, and adoption weights are all required.';
    else if (!nearlyEqual(arr + mbo + nps + adop, 1.0)) e.weight_sum = 'ARR + MBO + NPS + adoption weights must equal 1.0.';
    return e;
  }, [componentForm]);

  const buildHeaderPayload = useCallback(() => {
    const fp = fiscalPeriods.find((p) => String(p.fiscal_period_id) === String(headerForm.fiscal_period_id));
    const effectiveDate = fp?.period_start_date ?? fp?.start_date ?? null;
    return {
      role_id: toIntegerOrNull(headerForm.role_id),
      segment_id: toIntegerOrNull(headerForm.segment_id),
      fiscal_period_id: toIntegerOrNull(headerForm.fiscal_period_id),
      plan_code: headerForm.plan_code.trim(),
      plan_name: headerForm.plan_name.trim(),
      business_unit_id: hasBusinessUnitScope ? toIntegerOrNull(headerForm.business_unit_id) : null,
      rollup_mode: isEnterprise ? headerForm.rollup_mode || null : null,
      effective_date: effectiveDate,
    };
  }, [hasBusinessUnitScope, headerForm, isEnterprise, fiscalPeriods]);

  const buildComponentPayload = useCallback((amdId) => {
    // OI-S387-02: derive effective_date from the selected plan's fiscal period start date.
    // Without this, the API defaults to CURRENT_DATE which is wrong for historical periods.
    const planFiscalPeriodId = selectedPlan?.fiscal_period_id;
    const fp = fiscalPeriods.find((p) => String(p.fiscal_period_id) === String(planFiscalPeriodId));
    const effectiveDate = fp?.period_start_date ?? fp?.start_date ?? null;

    const payload = {
      plan_id: toIntegerOrNull(selectedPlanId),
      role_id: toIntegerOrNull(selectedPlan?.role_id),
      segment_id: toIntegerOrNull(selectedPlan?.segment_id),
      ote_min: toNumberOrNull(componentForm.ote_min),
      ote_max: toNumberOrNull(componentForm.ote_max),
      ote_midpoint: (toNumberOrNull(componentForm.ote_min) !== null && toNumberOrNull(componentForm.ote_max) !== null)
        ? (toNumberOrNull(componentForm.ote_min) + toNumberOrNull(componentForm.ote_max)) / 2 : null,
      ote_currency_code: componentForm.ote_currency_code?.trim() || null,
      // §17: ote_source = 'manual' when CA provides OTE; null when all fields blank.
      ote_source: toNumberOrNull(componentForm.ote_min) !== null ? 'manual' : null,
      base_pct: toNumberOrNull(componentForm.base_pct),
      variable_pct: toNumberOrNull(componentForm.variable_pct),
      arr_weight: toNumberOrNull(componentForm.arr_weight),
      mbo_weight: toNumberOrNull(componentForm.mbo_weight),
      nps_weight: toNumberOrNull(componentForm.nps_weight),
      adoption_weight: toNumberOrNull(componentForm.adoption_weight),
      effective_date: effectiveDate,
    };
    if (amdId) payload.amendment_id = amdId;
    return payload;
  }, [componentForm, fiscalPeriods, selectedPlan, selectedPlanId]);

  // Plan header create — NOT governed (plan is not in amendment_stage_impact_map)
  const createPlan = useCallback(async (event) => {
    event.preventDefault();
    const errors = validateHeader();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setSaving(true); setInlineError('');
    try {
      const created = await fetchJson(`${API_BASE}/plans`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildHeaderPayload()),
      });
      if (created) { setPlans((p) => [created, ...p]); setSelectedPlanId(created.plan_id); }
      else await loadPlans();
      setStep(2); showToast('Plan header saved.');
    } catch (e) { setInlineError(getErrorMessage(e, 'Unable to save plan header.')); }
    finally { setSaving(false); }
  }, [buildHeaderPayload, fetchJson, loadPlans, showToast, validateHeader]);

  // Plan component create — GOVERNED
  const handleCreateComponentClick = (event) => {
    event.preventDefault();
    if (!selectedPlanId) { setInlineError('Select or create a plan before adding components.'); return; }
    const errors = validateComponent();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (amendmentId) { runCreateComponent(amendmentId); return; }
    // During onboarding activeFiscalPeriodId is null — bypass amendment gate.
    if (!propFiscalPeriodId) { runCreateComponent(null); return; }
    setPendingAction('create_component');
    setAmendModalOpen(true);
  };

  const runCreateComponent = async (amdId) => {
    setSaving(true); setInlineError('');
    try {
      const created = await fetchJson(`${API_BASE}/plan-components`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildComponentPayload(amdId)),
      });
      if (created) setPlanComponents((p) => [created, ...p]);
      else await loadPlanComponents();
      if (amdId) { await commitAmendment(amdId); setAmendmentId(null); }
      setComponentForm(EMPTY_COMPONENT_FORM);
      showToast('Plan component saved. It remains engine-excluded until approved.');
    } catch (e) { setInlineError(getErrorMessage(e, 'Unable to save plan component.')); }
    finally { setSaving(false); }
  };

  // Plan component update — PATCH effective_date (and future fields) on existing component
  // Not amendment-governed during onboarding (session_status='active'). Server enforces Gate 5.
  const runUpdateComponent = async () => {
    if (!editingComponentId) return;
    const errors = validateComponent();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setSaving(true); setInlineError('');
    try {
      const fp = fiscalPeriods.find((p) => String(p.fiscal_period_id) === String(selectedPlan?.fiscal_period_id));
      const fiscalPeriodId = toIntegerOrNull(selectedPlan?.fiscal_period_id);
      // effective_date: use form value if supplied, otherwise fall back to period_start_date
      const effectiveDate = componentForm.effective_date
        || fp?.period_start_date
        || fp?.start_date
        || null;
      const updated = await fetchJson(`${API_BASE}/plan-components/${editingComponentId}/effective-date`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ effective_date: effectiveDate, fiscal_period_id: fiscalPeriodId }),
      });
      if (updated) setPlanComponents((p) => p.map((c) => c.plan_component_id === editingComponentId ? { ...c, ...updated } : c));
      else await loadPlanComponents();
      setEditingComponentId(null);
      setComponentForm(EMPTY_COMPONENT_FORM);
      setStep(null);
      showToast('Plan component updated.');
    } catch (e) { setInlineError(getErrorMessage(e, 'Unable to update plan component.')); }
    finally { setSaving(false); }
  };

  // Approve plan component — GOVERNED only if already approved (re-approval = amendment)
  // First-time approval (approved_by IS NULL) bypasses amendment gate — not a governed mutation.
  // Amendment gate only fires when modifying an already-approved component.
  const handleApproveComponentClick = (planComponentId) => {
    const component = planComponents.find((c) => c.plan_component_id === planComponentId);
    const isAlreadyApproved = Boolean(component?.approved_by);
    if (amendmentId) { runApprovePlanComponent(planComponentId, amendmentId); return; }
    if (isAlreadyApproved) {
      // Re-approval of governed entity — amendment required
      setPendingAction({ type: 'approve_component', id: planComponentId });
      setAmendModalOpen(true);
      return;
    }
    // First-time approval — bypass amendment gate, approve directly
    runApprovePlanComponent(planComponentId, null);
  };

  const runApprovePlanComponent = async (planComponentId, amdId) => {
    setSaving(true); setInlineError('');
    try {
      // fiscal_period_id is required by governed mutation endpoint even during onboarding.
      // Resolve from the selected plan's fiscal_period_id.
      const body = {};
      if (amdId) body.amendment_id = amdId;
      const plan = plans.find((p) => String(p.plan_id) === String(selectedPlanId));
      if (plan?.fiscal_period_id) body.fiscal_period_id = plan.fiscal_period_id;
      const updated = await fetchJson(`${API_BASE}/plan-components/${planComponentId}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      setPlanComponents((p) => p.map((c) => c.plan_component_id === planComponentId ? { ...c, ...(updated || {}) } : c));
      if (amdId) { await commitAmendment(amdId); setAmendmentId(null); }
      showToast('Plan component approved. It is now engine-eligible.');
    } catch (e) { setInlineError(getErrorMessage(e, 'Unable to approve plan component.')); }
    finally { setSaving(false); }
  };

  // Amendment modal handler
  const handleAmendmentBegin = (newAmendmentId) => {
    setAmendmentId(newAmendmentId);
    setAmendModalOpen(false);
    if (pendingAction === 'create_component') {
      runCreateComponent(newAmendmentId);
    } else if (pendingAction?.type === 'approve_component') {
      runApprovePlanComponent(pendingAction.id, newAmendmentId);
    }
    setPendingAction(null);
  };

  // Plan-level actions (not governed)
  const submitPlan = useCallback(async (planId) => {
    setSaving(true); setInlineError('');
    try {
      const updated = await fetchJson(`${API_BASE}/plans/${planId}/submit`, { method: 'POST' });
      setPlans((p) => p.map((plan) => plan.plan_id === planId ? { ...plan, ...(updated || {}), plan_status: updated?.plan_status || 'pending_approval' } : plan));
      showToast('Plan submitted for approval.');
    } catch (e) { setInlineError(getErrorMessage(e, 'Unable to submit plan.')); }
    finally { setSaving(false); }
  }, [fetchJson, showToast]);

  // GAP-APPROVAL-01 (AD-42): approvePlan() removed. Plan status is DERIVED from
  // component approval state via _derive_plan_status() in plans_router.py.
  // Direct plan approval must not be called from any UI. Approvals happen per
  // plan_component via POST /plan-components/{id}/approve in the Approvals tab.

  const rejectPlan = useCallback(async (planId) => {
    if (!rejectionReason.trim()) { setInlineError('Rejection reason is required.'); return; }
    setSaving(true); setInlineError('');
    try {
      const updated = await fetchJson(`${API_BASE}/plans/${planId}/reject`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejection_reason: rejectionReason.trim() }),
      });
      setPlans((p) => p.map((plan) => plan.plan_id === planId ? { ...plan, ...(updated || {}), plan_status: updated?.plan_status || 'draft' } : plan));
      setRejectingPlanId(null); setRejectionReason('');
      showToast('Plan rejected and returned to draft.');
    } catch (e) { setInlineError(getErrorMessage(e, 'Unable to reject plan.')); }
    finally { setSaving(false); }
  }, [fetchJson, rejectionReason, showToast]);

  // S471 — CSV import handlers (mirrors CreditDataReview / QuotaDataReview pattern)

  const PLAN_REQUIRED_COLS = [
    'plan_code', 'plan_name', 'role_code', 'segment_code', 'fiscal_period_code',
    'ote_min', 'ote_midpoint', 'ote_max', 'ote_currency_code',
    'base_pct', 'variable_pct',
    'arr_weight', 'mbo_weight', 'nps_weight', 'adoption_weight',
  ];

  const ensurePlanColumnMapping = useCallback(async () => {
    // Mirrors _REQUIRED_TARGETS["plan"] in import_service.py exactly.
    // All 15 required fields. rollup_mode optional (blank → NULL for IC/AE/CSM/SE).
    const mappings = [
      { source_column_name: 'plan_code',           target_column_name: 'plan_code',           is_required: true  },
      { source_column_name: 'plan_name',           target_column_name: 'plan_name',           is_required: true  },
      { source_column_name: 'role_code',           target_column_name: 'role_code',           is_required: true  },
      { source_column_name: 'segment_code',        target_column_name: 'segment_code',        is_required: true  },
      { source_column_name: 'fiscal_period_code',  target_column_name: 'fiscal_period_code',  is_required: true  },
      { source_column_name: 'ote_min',             target_column_name: 'ote_min',             is_required: true  },
      { source_column_name: 'ote_midpoint',        target_column_name: 'ote_midpoint',        is_required: true  },
      { source_column_name: 'ote_max',             target_column_name: 'ote_max',             is_required: true  },
      { source_column_name: 'ote_currency_code',   target_column_name: 'ote_currency_code',   is_required: true  },
      { source_column_name: 'base_pct',            target_column_name: 'base_pct',            is_required: true  },
      { source_column_name: 'variable_pct',        target_column_name: 'variable_pct',        is_required: true  },
      { source_column_name: 'arr_weight',          target_column_name: 'arr_weight',          is_required: true  },
      { source_column_name: 'mbo_weight',          target_column_name: 'mbo_weight',          is_required: true  },
      { source_column_name: 'nps_weight',          target_column_name: 'nps_weight',          is_required: true  },
      { source_column_name: 'adoption_weight',     target_column_name: 'adoption_weight',     is_required: true  },
      { source_column_name: 'rollup_mode',         target_column_name: 'rollup_mode',         is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'plan', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed plan column mapping.');
  }, []);

  const runCsvPlanValidate = useCallback(async (file) => {
    if (!file) return;
    setCsvPlanImporting(true);
    setCsvPlanError('');
    setCsvPlanDryResult(null);
    setCsvPlanCommitted(false);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvPlanError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = PLAN_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) {
        setCsvPlanError(`Missing required columns: ${missing.join(', ')}`);
        return;
      }
      const warnings = [];
      if (!headers.includes('rollup_mode')) {
        warnings.push('rollup_mode absent — all plans will have no rollup (correct for IC/AE/CSM/SE).');
      }
      setCsvPlanDryResult({ rows: lines.length - 1, warnings, errors: [] });
    } catch { setCsvPlanError('Failed to parse CSV.'); }
    finally { setCsvPlanImporting(false); }
  }, []);

  const runCsvPlanCommit = useCallback(async (file) => {
    if (!file || !csvPlanDryResult) return;
    setCsvPlanImporting(true);
    setCsvPlanError('');
    try {
      await ensurePlanColumnMapping();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'plan');
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json();
      if (!res.ok) { setCsvPlanError(json?.message || json?.detail || `Error ${res.status}`); return; }
      setCsvPlanCommitted(true);
      setCsvPlanDryResult(null);
      setCsvPlanFile(null);
      showToast(`${json.import_log?.rows_loaded ?? csvPlanDryResult.rows} plan(s) created in draft — a second comp_admin must approve each.`);
      await loadPlans();
    } catch (e) { setCsvPlanError(e.message || 'Commit failed.'); }
    finally { setCsvPlanImporting(false); }
  }, [csvPlanDryResult, ensurePlanColumnMapping, loadPlans, showToast]);

  const selectedPlanComponents = useMemo(() => planComponents.filter((c) => String(c.plan_id) === String(selectedPlanId)), [planComponents, selectedPlanId]);

  // Resolve activeFiscalPeriodId: prop → selected plan → header form.
  // After plan header is saved the form is cleared; fall back to the selected
  // plan's fiscal_period_id so the amendment modal has a valid period.
  const activeFiscalPeriodId = propFiscalPeriodId
    ?? toIntegerOrNull(selectedPlan?.fiscal_period_id)
    ?? toIntegerOrNull(headerForm.fiscal_period_id);

  const thClass = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
  const tdClass = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
  const btnPrimary = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
  const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
  const inputClass = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none';
  const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

  if (!user) return (
    <section className="flex flex-col gap-6">
      <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Plan Builder</h2>
      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">Authentication is required to build plans.</div>
    </section>
  );

  if (blockingError) return (
    <section className="flex flex-col gap-6">
      <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Plan Builder</h2>
      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{blockingError}</div>
    </section>
  );

  // §17 DR-027: OTE fields are optional budget annotation — rendered separately with mandatory label.
  // Removed from COMPONENT_FIELDS so they get their own section header and subtext below.
  const COMPONENT_FIELDS = [
    { key: 'base_pct', label: 'Base %', type: 'number', step: '0.0001' },
    { key: 'variable_pct', label: 'Variable %', type: 'number', step: '0.0001' },
    { key: 'arr_weight', label: 'ARR Weight', type: 'number', step: '0.0001' },
    { key: 'mbo_weight', label: 'MBO Weight', type: 'number', step: '0.0001' },
    { key: 'nps_weight', label: 'NPS Weight', type: 'number', step: '0.0001' },
    { key: 'adoption_weight', label: 'Adoption Weight', type: 'number', step: '0.0001' },
  ];

  return (
    <section className="flex flex-col gap-6">

      {/* Amendment Begin Modal */}
      <AmendmentBeginModal
        open={amendModalOpen}
        entityType={GOVERNED_ENTITY_TYPE}
        entityId={pendingAction?.type === 'approve_component' ? String(pendingAction.id) : null}
        fiscalPeriodId={activeFiscalPeriodId}
        wizardSessionId={wizardSessionId ?? null}
        onBegin={handleAmendmentBegin}
        onCancel={() => { setAmendModalOpen(false); setPendingAction(null); }}
      />

      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">SETUP-04</p>
          <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Plan Builder</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">Create compensation plans and define plan components. Components must be approved before the engine can use them.</p>
        </div>
      </header>

      {toast && <div role="status" className="rounded-lg bg-[rgb(var(--color-primary))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-primary))] fixed bottom-4 right-4 z-50 shadow-lg">{toast}</div>}
      {inlineError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{inlineError}</div>}
      {amendmentId && (
        <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))] flex items-center justify-between">
          <span>⚠ Amendment open — plan component changes are governed. Save to commit.</span>
          <button type="button" onClick={async () => {
            try { await fetch(`${API_BASE}/amendments/${amendmentId}/cancel`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }); } catch (_) {}
            setAmendmentId(null);
          }} className="ml-3 text-tok-xs underline hover:no-underline">Cancel amendment</button>
        </div>
      )}

      {/* ── S471 CSV Bulk Import (OI-PLANS-WEBAPP-IMPORT) — collapsible ─────── */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm">
        <button
          type="button"
          onClick={() => setCsvImportOpen((v) => !v)}
          className="flex w-full items-center justify-between px-5 py-3 text-left"
        >
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</span>
            {csvPlanCommitted && <span className="text-tok-xs text-[rgb(var(--color-success))]">✓ Loaded</span>}
          </div>
          <ChevronDown className={`h-4 w-4 text-[rgb(var(--color-text-muted))] transition-transform ${csvImportOpen ? 'rotate-180' : ''}`} />
        </button>

        {csvImportOpen && (
        <div className="border-t border-[rgb(var(--color-border))] p-5 flex flex-col gap-5">
        <div>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Upload <strong>Plans.csv</strong> to create multiple plans and their components in one pass. Validates before committing — review results before confirming.
          </p>
        </div>

        {csvPlanError ? (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {csvPlanError}
          </div>
        ) : null}

        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">1</span>
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              Plans.csv — plan headers and components</span>
          </div>

          {/* Field contract hint */}
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Required columns: <code>plan_code, plan_name, role_code, segment_code, fiscal_period_code, ote_min, ote_midpoint, ote_max, ote_currency_code, base_pct, variable_pct, arr_weight, mbo_weight, nps_weight, adoption_weight</code>
            <span className="block mt-1">Optional: <code>rollup_mode</code> (blank → no rollup; valid values: manager_hierarchy, geo_region)</span>
            <span className="block mt-1 text-amber-600">⚠ Plans are created in draft status — a second comp_admin must approve each plan and its component before the engine can use them.</span>
          </p>

          {isLocked ? (
            <div className="flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-400/8 px-3 py-2.5">
              <Lock className="h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-tok-sm text-amber-600">Period is locked — plan imports are not available. Switch to an open period to import plans.</p>
            </div>
          ) : (
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setCsvPlanFile(f);
                setCsvPlanDryResult(null);
                setCsvPlanCommitted(false);
                setCsvPlanError('');
              }}
            />
            <button
              type="button"
              className={btnSecondary}
              disabled={!csvPlanFile || csvPlanImporting}
              onClick={() => runCsvPlanValidate(csvPlanFile)}
            >
              {csvPlanImporting && !csvPlanDryResult ? 'Validating…' : 'Validate'}
            </button>
          </div>
          )}

          {csvPlanDryResult ? (
            <div className="mt-1 flex flex-col gap-2">
              <div className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Rows parsed: <strong>{csvPlanDryResult.rows}</strong> · Plans to create: <strong>{csvPlanDryResult.rows}</strong>
              </div>
              {csvPlanDryResult.warnings?.map((w, i) => (
                <p key={i} className="text-tok-xs text-amber-600">⚠ {w}</p>
              ))}
              {csvPlanDryResult.errors?.length > 0 ? (
                <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvPlanDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">
                    ✓ Validation passed — {csvPlanDryResult.rows} plan(s) ready to create.
                  </span>
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={csvPlanImporting}
                    onClick={() => runCsvPlanCommit(csvPlanFile)}
                  >
                    {csvPlanImporting ? 'Creating…' : 'Confirm and create'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
        </div>
        )}
      </div>

      {/* Step 1: Plan header — only shown when explicitly requested via New Plan button */}
      {step === 1 && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Plan header</h3>
          <form onSubmit={createPlan}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className={labelClass}>Role<select className={inputClass} value={headerForm.role_id} onChange={(e) => updateHeaderForm('role_id', e.target.value)} disabled={referenceLoading}><option value="">Select role</option>{roles.map((r) => <option key={r.role_id} value={r.role_id}>{r.role_name || r.name}</option>)}</select>{validationErrors.role_id && <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.role_id}</small>}</label>
              <label className={labelClass}>Segment<select className={inputClass} value={headerForm.segment_id} onChange={(e) => updateHeaderForm('segment_id', e.target.value)} disabled={referenceLoading}><option value="">Select segment</option>{segments.map((s) => <option key={s.segment_id} value={s.segment_id}>{s.segment_name || s.name}</option>)}</select>{validationErrors.segment_id && <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.segment_id}</small>}</label>
              <label className={labelClass}>Fiscal Period<select className={inputClass} value={headerForm.fiscal_period_id} onChange={(e) => updateHeaderForm('fiscal_period_id', e.target.value)} disabled={referenceLoading}><option value="">Select period</option>{fiscalPeriods.map((p) => <option key={p.fiscal_period_id} value={p.fiscal_period_id}>{p.period_label || `Period ${p.fiscal_period_id}`}</option>)}</select>{validationErrors.fiscal_period_id && <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.fiscal_period_id}</small>}</label>
              {hasBusinessUnitScope && <label className={labelClass}>Business Unit<select className={inputClass} value={headerForm.business_unit_id} onChange={(e) => updateHeaderForm('business_unit_id', e.target.value)}><option value="">Global</option>{businessUnits.map((b) => <option key={b.business_unit_id} value={b.business_unit_id}>{b.business_unit_name || b.name}</option>)}</select></label>}
              <label className={labelClass}>Plan Code<input type="text" className={inputClass} value={headerForm.plan_code} onChange={(e) => updateHeaderForm('plan_code', e.target.value)} />{validationErrors.plan_code && <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.plan_code}</small>}</label>
              <label className={labelClass}>Plan Name<input type="text" className={inputClass} value={headerForm.plan_name} onChange={(e) => updateHeaderForm('plan_name', e.target.value)} />{validationErrors.plan_name && <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.plan_name}</small>}</label>
              {isEnterprise && <label className={labelClass}>Rollup Mode<input type="text" className={inputClass} value={headerForm.rollup_mode} onChange={(e) => updateHeaderForm('rollup_mode', e.target.value)} /></label>}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button type="submit" className={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save plan header'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Step 2: Plan components — create or edit */}
      {step === 2 && selectedPlanId && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
              {editingComponentId
                ? `Edit component for ${plans.find((p) => String(p.plan_id) === String(selectedPlanId))?.plan_code || selectedPlanId}`
                : `Add component for ${plans.find((p) => String(p.plan_id) === String(selectedPlanId))?.plan_code || selectedPlanId}`}
            </h3>
            <button type="button" className={btnSecondary} onClick={() => {
              setStep(null); setSelectedPlanId(null); setHeaderForm(EMPTY_HEADER_FORM);
              setComponentForm(EMPTY_COMPONENT_FORM); setEditingComponentId(null); setValidationErrors({});
            }}>+ New plan</button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); editingComponentId ? runUpdateComponent() : handleCreateComponentClick(e); }}>

            {/* ── OTE Range — Budget Annotation (Optional) ── §17 DR-027 mandatory section ── */}
            <div className="mb-4 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]/40 p-4">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))] mb-0.5">OTE Range — Budget Annotation (Optional)</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-3 leading-relaxed">
                OTE targets for budget modelling only. Not used in payout calculation — OTE is sourced from position records at calculation time. Leave all fields blank to skip.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className={labelClass}>
                  OTE Min
                  <input type="number" step="0.01" className={inputClass} value={componentForm.ote_min} onChange={(e) => updateComponentForm('ote_min', e.target.value)} placeholder="e.g. 180000" />
                </label>
                <label className={labelClass}>
                  OTE Max
                  <input type="number" step="0.01" className={inputClass} value={componentForm.ote_max} onChange={(e) => updateComponentForm('ote_max', e.target.value)} placeholder="e.g. 240000" />
                  {validationErrors.ote_max && <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.ote_max}</small>}
                </label>
                <label className={labelClass}>
                  OTE Currency
                  <input type="text" className={inputClass} value={componentForm.ote_currency_code} onChange={(e) => updateComponentForm('ote_currency_code', e.target.value)} placeholder="e.g. USD" maxLength={10} />
                </label>
              </div>
              {validationErrors.ote_band && <small className="mt-2 block text-tok-xs text-[rgb(var(--color-danger))]">{validationErrors.ote_band}</small>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {COMPONENT_FIELDS.map(({ key, label, type, step: s }) => (
                <label key={key} className={labelClass}>
                  {label}
                  <input type={type} step={s} className={inputClass} value={componentForm[key]} onChange={(e) => updateComponentForm(key, e.target.value)} />
                  {validationErrors[key] && <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors[key]}</small>}
                </label>
              ))}
              {/* effective_date — always shown; required for engine eligibility (S572) */}
              <label className={labelClass}>
                Effective Date
                <input
                  type="date"
                  className={inputClass}
                  value={componentForm.effective_date}
                  onChange={(e) => updateComponentForm('effective_date', e.target.value)}
                />
                <small className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1">
                  Must be ≤ period start date. Engine ignores components with a later date.
                </small>
                {validationErrors.effective_date && <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.effective_date}</small>}
              </label>
            </div>
            {validationErrors.base_variable_sum && <div className="mt-2 rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{validationErrors.base_variable_sum}</div>}
            {validationErrors.weight_sum && <div className="mt-2 rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{validationErrors.weight_sum}</div>}
            <div className="flex items-center gap-2 mt-4">
              <button type="submit" className={btnPrimary} disabled={saving || !selectedPlanId}>
                {saving ? 'Saving…' : editingComponentId ? 'Save changes' : 'Save component'}
              </button>
              {editingComponentId && (
                <button type="button" className={btnSecondary} onClick={() => {
                  setEditingComponentId(null); setComponentForm(EMPTY_COMPONENT_FORM);
                  setValidationErrors({}); setStep(null);
                }}>Cancel</button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Plans table */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Plans</h3>
          <div className="flex items-center gap-2">
            {loading && <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</span>}
            <button type="button" className={btnSecondary} onClick={loadPlans} disabled={loading}>Refresh</button>
            {!isLocked && (
              <button
                type="button"
                className={btnPrimary}
                onClick={() => {
                  setStep(step === 1 ? null : 1);
                  setHeaderForm(EMPTY_HEADER_FORM);
                  setValidationErrors({});
                  setInlineError('');
                  setSelectedPlanId(null);
                }}
              >
                {step === 1 ? '✕ Cancel' : '+ New Plan'}
              </button>
            )}
          </div>
        </div>
        {plans.length === 0 && !loading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No plans have been created.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-tok-sm border-collapse">
              <thead><tr>
                <th className={thClass}>Code</th><th className={thClass}>Name</th><th className={thClass}>Role</th><th className={thClass}>Segment</th><th className={thClass}>Fiscal period</th>
                {hasBusinessUnitScope && <th className={thClass}>Business unit</th>}
                {isEnterprise && <th className={thClass}>Rollup</th>}
                <th className={thClass}>Status</th><th className={thClass}>Engine eligibility</th><th className={thClass} aria-label="Actions" />
              </tr></thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.plan_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className={tdClass}>{plan.plan_code}</td>
                    <td className={tdClass}>{plan.plan_name}</td>
                    <td className={tdClass}>{roleNameById[String(plan.role_id)] || plan.role_id}</td>
                    <td className={tdClass}>{segmentNameById[String(plan.segment_id)] || plan.segment_id}</td>
                    <td className={tdClass}>{fiscalPeriodNameById[String(plan.fiscal_period_id)] || plan.fiscal_period_id}</td>
                    {hasBusinessUnitScope && <td className={tdClass}>{businessUnitNameById[String(plan.business_unit_id)] || plan.business_unit_id || 'Global'}</td>}
                    {isEnterprise && <td className={tdClass}>{plan.rollup_mode || 'None'}</td>}
                    <td className={tdClass}>{plan.plan_status || 'draft'}</td>
                    <td className={tdClass}>{plan.plan_status === 'approved' ? 'Engine-eligible' : 'Engine-excluded'}</td>
                    <td className={tdClass}>
                      <div className="flex items-center gap-1">
                        <button type="button" className={btnSecondary} onClick={() => {
                          setSelectedPlanId(plan.plan_id);
                          setStep(2);
                          setValidationErrors({});
                          setInlineError('');
                          // If a component already exists for this plan, load it into the form for editing
                          const existing = planComponents.find((c) => String(c.plan_id) === String(plan.plan_id));
                          if (existing) {
                            setEditingComponentId(existing.plan_component_id);
                            setComponentForm({
                              ote_min:           String(existing.ote_min ?? ''),
                              ote_max:           String(existing.ote_max ?? ''),
                              ote_midpoint:      String(existing.ote_midpoint ?? ''),
                              ote_currency_code: existing.ote_currency_code ?? '',
                              base_pct:          String(existing.base_pct ?? ''),
                              variable_pct:      String(existing.variable_pct ?? ''),
                              arr_weight:        String(existing.arr_weight ?? ''),
                              mbo_weight:        String(existing.mbo_weight ?? ''),
                              nps_weight:        String(existing.nps_weight ?? ''),
                              adoption_weight:   String(existing.adoption_weight ?? ''),
                              effective_date:    existing.effective_date ? existing.effective_date.slice(0, 10) : '',
                            });
                          } else {
                            setEditingComponentId(null);
                            setComponentForm(EMPTY_COMPONENT_FORM);
                          }
                        }}>Components</button>
                        <button type="button" className={btnSecondary} onClick={() => submitPlan(plan.plan_id)} disabled={saving || plan.plan_status !== 'draft'}>Submit</button>
                        {/* Approve button removed — GAP-APPROVAL-01 / AD-42.
                            Plan approval is derived from component approval state.
                            Use the Approvals tab (CA-2) to approve plan_components. */}
                        <button type="button" className={btnSecondary} onClick={() => { setRejectingPlanId(plan.plan_id); setRejectionReason(''); }} disabled={saving || plan.plan_status !== 'pending_approval'}>Reject</button>
                      </div>
                      {rejectingPlanId === plan.plan_id && (
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <input type="text" className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm focus:outline-none" placeholder="Rejection reason" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
                          <button type="button" className={btnPrimary} onClick={() => rejectPlan(plan.plan_id)} disabled={saving}>Confirm reject</button>
                          <button type="button" className={btnSecondary} onClick={() => setRejectingPlanId(null)}>Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Components for selected plan */}
      {selectedPlanId && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Components for selected plan</h3>
          {selectedPlanComponents.length === 0 ? (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No components have been saved for this plan.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead><tr>
                  <th className={thClass}>OTE range</th><th className={thClass}>Base / variable</th><th className={thClass}>Weights</th><th className={thClass}>Effective date</th><th className={thClass}>Engine eligibility</th><th className={thClass} aria-label="Actions" />
                </tr></thead>
                <tbody>
                  {selectedPlanComponents.map((component) => (
                    <tr key={component.plan_component_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass}>{component.ote_min}–{component.ote_max}</td>
                      <td className={tdClass}>{component.base_pct} / {component.variable_pct}</td>
                      <td className={tdClass}>ARR {component.arr_weight}, MBO {component.mbo_weight}, NPS {component.nps_weight}, Adoption {component.adoption_weight}</td>
                      <td className={tdClass}>{component.effective_date ? component.effective_date.slice(0, 10) : '—'}</td>
                      <td className={tdClass}>{component.approved_by ? 'Engine-eligible' : 'Engine-excluded until approved'}</td>
                      <td className={tdClass}>
                        <div className="flex items-center gap-1">
                          <button type="button" className={btnSecondary} onClick={() => {
                            setSelectedPlanId(component.plan_id);
                            setEditingComponentId(component.plan_component_id);
                            setComponentForm({
                              ote_min:           String(component.ote_min ?? ''),
                              ote_max:           String(component.ote_max ?? ''),
                              ote_midpoint:      String(component.ote_midpoint ?? ''),
                              ote_currency_code: component.ote_currency_code ?? '',
                              base_pct:          String(component.base_pct ?? ''),
                              variable_pct:      String(component.variable_pct ?? ''),
                              arr_weight:        String(component.arr_weight ?? ''),
                              mbo_weight:        String(component.mbo_weight ?? ''),
                              nps_weight:        String(component.nps_weight ?? ''),
                              adoption_weight:   String(component.adoption_weight ?? ''),
                              effective_date:    component.effective_date ? component.effective_date.slice(0, 10) : '',
                            });
                            setValidationErrors({});
                            setInlineError('');
                            setStep(2);
                          }}>Edit</button>
                          <button type="button" className={btnSecondary} onClick={() => handleApproveComponentClick(component.plan_component_id)} disabled={saving || Boolean(component.approved_by)}>Approve</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Wizard-nav footer — only rendered when mounted inside DataSetupFlowShell.
          When mounted from ComponentsTab (CompensationPoliciesWorkspace), onBack and
          onNext are undefined — footer is suppressed to avoid broken button clicks. */}
      {(onBack || onNext) && (
        <footer className="flex items-center gap-3 pt-4 border-t border-[rgb(var(--color-border))]">
          {onBack && <button type="button" className={btnSecondary} onClick={onBack}>Back</button>}
          {onNext && <button type="button" className={btnPrimary} onClick={onNext}>Continue to rate tables</button>}
        </footer>
      )}
    </section>
  );
}

export default PlanBuilder;
